import { MantineProvider, AppShell, ActionIcon, Menu, Avatar, Loader, Center, Group, Switch, Tabs, Indicator, LoadingOverlay } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Notifications } from '@mantine/notifications';
import { AuthProvider, withAuthInfo, useLogoutFunction, WithLoggedInAuthInfoProps, RedirectToLogin } from '@propelauth/react';
import { memo, Suspense, useState, useEffect, lazy } from 'react';
import * as Sentry from "@sentry/react";
import { env, isProduction, isDebugMode, toggleDebugMode, isDevelopment } from './utils/env';
import { ErrorBoundary } from './components/ErrorBoundary';
import chartPieIcon from './assets/icons/chart-pie.svg';
import walletIcon from './assets/icons/wallet.svg';
import bankIcon from './assets/icons/bank.svg';
import bellIcon from './assets/icons/bell.svg';
import chartBarIcon from './assets/icons/chart-bar.svg';
import usersIcon from './assets/icons/users.svg';
import { useUnreadNotifications } from './hooks/useUnreadNotifications';
import TokenRefreshWrapper from './components/TokenRefreshWrapper';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Lazy load non-critical components
const BudgetTab = lazy(() => import('./components/BudgetTab'));
const TransactionTab = lazy(() => import('./components/TransactionTab'));
const AccountsTab = lazy(() => import('./components/AccountsTab'));
const NotificationTab = lazy(() => import('./components/NotificationTab'));
const GroupsTab = lazy(() => import('./components/GroupsTab'));
const GoalsTab = lazy(() => import('./components/GoalsTab'));
const PlaidOAuthRedirect = lazy(() => import('./components/PlaidOAuthRedirect'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Data is immediately considered stale
      gcTime: 300000, // Keep cached data for 5 minutes (was cacheTime in v4)
      refetchOnMount: true, // Always refetch when component mounts
      refetchOnWindowFocus: true, // Refetch when window regains focus
      retry: 1,
    },
  },
});

Sentry.init({
  dsn: env.SENTRY_DSN,
  environment: env.ENV,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: isProduction() ? 0.1 : 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: isProduction() ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0, // Always capture sessions with errors
});

// Memoize the menu component with props check
const UserMenu = memo(({ user, onLogout }: { user: any; onLogout: () => void }) => {
  const [isDebug, setIsDebug] = useState(isDebugMode());
  const isDev = isDevelopment();

  const handleDebugToggle = () => {
    const newState = toggleDebugMode();
    setIsDebug(newState);
  };

  if (!user) return null;

  return (
    <Menu position="bottom-end" shadow="md">
      <Menu.Target>
        <ActionIcon variant="subtle" size="lg" style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000 }}>
          <Avatar src={user.pictureUrl} radius="xl" size="md" />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {isDev && (
          <>
            <Menu.Item>
              <Group justify="space-between">
                <span>Debug Mode</span>
                <Switch
                  checked={isDebug}
                  onChange={handleDebugToggle}
                  size="sm"
                />
              </Group>
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
        <Menu.Item onClick={onLogout}>Logout</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}, (prevProps, nextProps) => prevProps.user?.pictureUrl === nextProps.user?.pictureUrl);

// Single auth wrapper that passes user data down
const AuthenticatedApp = withAuthInfo((props: WithLoggedInAuthInfoProps) => {
  const logout = useLogoutFunction();
  const handleLogout = () => logout(true);
  const [isDebug, setIsDebug] = useState(isDebugMode());
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Valid tab values
    const validTabs = ['accounts', 'budget', 'goals', 'transactions', 'groups', 'notifications'];

    // Check URL hash first (most reliable for sharing links)
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      if (validTabs.includes(hash)) {
        return hash;
      }
    }

    // Then try localStorage
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab && validTabs.includes(savedTab)) {
      return savedTab;
    }

    // Default to accounts if nothing saved
    return 'accounts';
  });
  const { unreadCount } = useUnreadNotifications(props.accessToken);

  // Debug logging for access token
  useEffect(() => {
    console.log('[AuthenticatedApp] AccessToken present:', !!props.accessToken);
    console.log('[AuthenticatedApp] AccessToken length:', props.accessToken?.length || 0);
    console.log('[AuthenticatedApp] Is user logged in:', props.isLoggedIn);
  }, [props.accessToken, props.isLoggedIn]);

  // Check token validity periodically in development mode
  useEffect(() => {
    if (isDevelopment() && props.isLoggedIn && props.accessToken) {
      // Periodically check token validity in development
      const checkInterval = setInterval(() => {
        try {
          // Attempt to decode token to check expiration (just for debugging)
          const base64Url = props.accessToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const tokenData = JSON.parse(atob(base64));

          if (tokenData.exp) {
            const expiresAt = new Date(tokenData.exp * 1000);
            const now = new Date();
            const minutesLeft = Math.round((expiresAt.getTime() - now.getTime()) / 60000);

            if (minutesLeft < 30) {
              console.log(`[AuthenticatedApp] Token expires in ${minutesLeft} minutes`, {
                expiresAt: expiresAt.toISOString()
              });
            }
          }
        } catch (error) {
          // Just for debugging, ignore errors
        }
      }, 60000); // Check every minute

      return () => clearInterval(checkInterval);
    }
  }, [props.isLoggedIn, props.accessToken]);

  // Subscribe to debug mode changes
  useEffect(() => {
    const updateDebugState = () => setIsDebug(isDebugMode());
    window.addEventListener('debug-mode-change', updateDebugState);
    return () => window.removeEventListener('debug-mode-change', updateDebugState);
  }, []);

  // If not authenticated, redirect to login
  if (!props.isLoggedIn) {
    return <RedirectToLogin />;
  }

  const handleTabChange = (value: string | null) => {
    if (value === null) return; // Don't do anything if value is null

    setActiveTab(value);
    // Update URL hash for bookmarking and sharing
    window.location.hash = value;
    // Save to localStorage for persistence between sessions
    localStorage.setItem('activeTab', value);
  };

  return (
    <AppShell padding="md">
      <UserMenu user={props.user} onLogout={handleLogout} />
      <AppShell.Main>
        <Group align="flex-start" gap="md">
          <div style={{ flex: 1 }}>
            <Suspense fallback={
              <Center style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                <Loader size="lg" />
              </Center>
            }>
              <Tabs value={activeTab} onChange={handleTabChange} mb="md">
                <Tabs.List>
                  <Tabs.Tab value="accounts" leftSection={
                    <img
                      src={bankIcon}
                      alt="Accounts"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        filter: 'none',
                        opacity: 0.6
                      }}
                    />
                  }>
                    Accounts
                  </Tabs.Tab>
                  <Tabs.Tab value="budget" leftSection={
                    <img
                      src={chartPieIcon}
                      alt="Budget"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        filter: 'none',
                        opacity: 0.6
                      }}
                    />
                  }>
                    Budget
                  </Tabs.Tab>
                  <Tabs.Tab value="goals" leftSection={
                    <img
                      src={chartBarIcon}
                      alt="Goals"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        filter: 'none',
                        opacity: 0.6
                      }}
                    />
                  }>
                    Goals
                  </Tabs.Tab>
                  <Tabs.Tab value="transactions" leftSection={
                    <img
                      src={walletIcon}
                      alt="Transactions"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        filter: 'none',
                        opacity: 0.6
                      }}
                    />
                  }>
                    Transactions
                  </Tabs.Tab>
                  <Tabs.Tab value="groups" leftSection={
                    <img
                      src={usersIcon}
                      alt="Groups"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        filter: 'none',
                        opacity: 0.6
                      }}
                    />
                  }>
                    Groups
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="notifications"
                    leftSection={
                      <Indicator
                        inline
                        label={unreadCount > 0 ? unreadCount.toString() : ''}
                        size={16}
                        disabled={!unreadCount}
                        processing={false}
                      >
                        <img
                          src={bellIcon}
                          alt="Notifications"
                          style={{
                            width: '1rem',
                            height: '1rem',
                            filter: 'none',
                            opacity: 0.6
                          }}
                        />
                      </Indicator>
                    }
                  >
                    Notifications
                  </Tabs.Tab>
                </Tabs.List>

                <div style={{ position: 'relative' }}>
                  <div style={{ display: activeTab === 'accounts' ? 'block' : 'none' }}>
                    <Suspense fallback={<LoadingOverlay visible />}>
                      <AccountsTab
                        accessToken={props.accessToken}
                        user={props.user}
                        isVisible={activeTab === 'accounts'}
                      />
                    </Suspense>
                  </div>
                  <div style={{ display: activeTab === 'budget' ? 'block' : 'none' }}>
                    <Suspense fallback={<LoadingOverlay visible />}>
                      <BudgetTab
                        accessToken={props.accessToken}
                        user={props.user}
                        isVisible={activeTab === 'budget'}
                      />
                    </Suspense>
                  </div>
                  <div style={{ display: activeTab === 'goals' ? 'block' : 'none' }}>
                    <Suspense fallback={<LoadingOverlay visible />}>
                      <GoalsTab
                        user={props.user}
                        accessToken={props.accessToken}
                        isVisible={activeTab === 'goals'}
                      />
                    </Suspense>
                  </div>
                  <div style={{ display: activeTab === 'transactions' ? 'block' : 'none' }}>
                    <Suspense fallback={<LoadingOverlay visible />}>
                      <TransactionTab
                        accessToken={props.accessToken}
                        user={props.user}
                        groupId={0}
                        isVisible={activeTab === 'transactions'}
                      />
                    </Suspense>
                  </div>
                  <div style={{ display: activeTab === 'groups' ? 'block' : 'none' }}>
                    <Suspense fallback={<Loader />}>
                      <GroupsTab
                        user={props.user}
                        accessToken={props.accessToken}
                        isVisible={activeTab === 'groups'}
                      />
                    </Suspense>
                  </div>
                  <div style={{ display: activeTab === 'notifications' ? 'block' : 'none' }}>
                    <Suspense fallback={<Loader />}>
                      <NotificationTab
                        user={props.user}
                        accessToken={props.accessToken}
                        isVisible={activeTab === 'notifications'}
                      />
                    </Suspense>
                  </div>
                </div>
              </Tabs>
            </Suspense>
          </div>
        </Group>
      </AppShell.Main>
    </AppShell>
  );
});

// Memoize the app content
const AppContent = memo(() => {
  return (
    <MantineProvider theme={{
      components: {
        TabsTab: {
          styles: {
            tab: {
              '&[data-active]': {
                'img': {
                  opacity: 1
                }
              }
            }
          }
        }
      }
    }}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider authUrl={env.PROPELAUTH_URL}>
            <TokenRefreshWrapper />
            <Notifications position="top-right" />
            <BrowserRouter>
              <Routes>
                <Route path="/plaid-oauth-redirect" element={
                  <Suspense fallback={<Center style={{ height: "100vh" }}><Loader size="xl" /></Center>}>
                    <PlaidOAuthRedirect />
                  </Suspense>
                } />
                <Route path="*" element={<AuthenticatedApp />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </MantineProvider>
  );
});

function App() {
  return <AppContent />;
}

export default App;
